import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// MUI Components
import {
  Typography, Paper, Box, TableContainer, Table, TableHead, TableRow,
  TableCell, Avatar, TextField, FormControl, InputLabel, Select,
  MenuItem, TableBody, Button, Collapse, Stack, CircularProgress, Tooltip,
  IconButton, InputAdornment,
  Rating,
} from "@mui/material";

// MUI Icons
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Project Utilities & Constants
import { API_BASE_URL } from "../../utils/constants";
// Ensure formatters are correct and accessible
import { formatNumber, displayBTCOrSats, transformToSats } from "./formatters";
// Ensure path is correct for QuickBuy component
import QuickBuy from "../Common/QuickBuy.component";
import { getStarsFromRating } from "../../utils/helpers";


// --- Constants ---
const MAX_EVENTS = 500; // Maximum number of events to keep in the list
const HOVER_DELAY_MS = 150; // Delay before showing QuickBuy on hover
const FILTER_DEBOUNCE_MS = 400; // Delay for applying filters after typing
const BATCH_PROCESS_INTERVAL_MS = 100; // <-- Process collected events every 100ms


// --- Helper Functions (no changes needed) ---
const getRowBackgroundColor = (type) => {
  switch (type) {
    case "sell": return "rgba(239, 83, 80, 0.5)"; // Soft Red
    case "buy": return "rgba(102, 187, 106, 0.5)";  // Soft Green
    case "create":
    default: return "rgba(66, 165, 245, 0.5)"; // Soft Blue
  }
};

export const ScoreCell = ({ userScore, title = "trader" }) => {
  if (!userScore) return <Typography variant="body2" >Unranked</Typography>;
  const { score, rating, percentile, comparedAgainst } = userScore;
  const stars = getStarsFromRating(rating);
  return <Rating
    name={`${ title }-rating`}
    value={stars}
    readOnly
    size="small"
    precision={0.5}
    sx={{
      padding: 0,
      margin: 0,
    }}
  />;
};


// --- Child Component: Event Row ---
// Keep React.memo and useCallback for performance within the row
const MemoizedEventRow = React.memo(function EventRow({ eventData }) {
  const {
    type = 'create', user, creator, amount_token, token,
    tokenid, amount_btc, time, name, id, // Added 'id' for 'create' type
    userScore
  } = eventData;



  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const effectiveUser = user || creator;
  const effectiveTokenId = type === "create" ? id : tokenid;

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setIsHovering(false), HOVER_DELAY_MS);
  }, []);

  const handleUserClick = useCallback((e) => {
    e.stopPropagation();
    if (effectiveUser) window.open(`/users/${ effectiveUser }`);
  }, [navigate, effectiveUser]);

  const handleTokenClick = useCallback((e) => {
    e.stopPropagation();
    if (effectiveTokenId) window.open(`/tokens/${ effectiveTokenId }`);
  }, [navigate, effectiveTokenId]);


  useEffect(() => () => { // Cleanup timeout on unmount
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  // Memoized render details for sell/buy/create
  const renderSellDetails = useCallback(() => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 150 }}>
      <Typography variant="body2" component="span" noWrap sx={{ fontWeight: 500 }}>
        <Button onClick={handleTokenClick} sx={{ fontWeight: "normal", p: 0, minWidth: 0 }}>{formatNumber(amount_token)} {token?.name}</Button> {/* Added Button styling */}
      </Typography>
      {tokenid && <Avatar alt={token?.name} src={`https://images.odin.fun/token/${ tokenid }`} sx={{ width: 20, height: 20, mx: 0.5 }} />} {/* Check tokenid */}
      <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" component="span" sx={{ ml: 0.5, fontWeight: 500 }}>
        {displayBTCOrSats(amount_btc)}
      </Typography>
    </Stack>
  ), [amount_token, token?.name, tokenid, amount_btc, handleTokenClick]); // Depend on props and handlers

  const renderBuyDetails = useCallback(() => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 150 }}>
      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
        {displayBTCOrSats(amount_btc)}
      </Typography>
      <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', mx: 0.5 }} />
      <Typography variant="body2" component="span" noWrap sx={{ fontWeight: 500 }}>
        <Button onClick={handleTokenClick} sx={{ fontWeight: "normal", p: 0, minWidth: 0 }}>{formatNumber(amount_token)} {token?.name}</Button> {/* Added Button styling */}
      </Typography>
      {tokenid && <Avatar alt={token?.name} src={`https://images.odin.fun/token/${ tokenid }`} sx={{ width: 20, height: 20, ml: 0.5 }} />} {/* Check tokenid */}
    </Stack>
  ), [amount_btc, amount_token, token?.name, tokenid, handleTokenClick]); // Depend on props and handlers

  const renderCreateDetails = useCallback(() => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" component="span">Created:</Typography>
      {id && <Avatar alt={name} src={`https://images.odin.fun/token/${ id }`} sx={{ width: 20, height: 20, cursor: 'pointer' }} onClick={handleTokenClick} />} {/* Check id, add cursor */}
      <Typography variant="body2" component="span" noWrap sx={{ fontWeight: 500 }}>{name}</Typography>
    </Stack>
  ), [name, id, handleTokenClick]); // Depend on props and handlers

  const eventDetailsContent = useMemo(() => {
    switch (type) {
      case "sell": return renderSellDetails();
      case "buy": return renderBuyDetails();
      case "create": default: return renderCreateDetails();
    }
  }, [type, renderSellDetails, renderBuyDetails, renderCreateDetails]); // Depend on type and memoized render functions

  const showQuickBuy = type === 'buy' || type === 'create';

  // --- JSX ---
  return (
    <>
      {/* Main Row */}
      <TableRow hover
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          bgcolor: getRowBackgroundColor(type),
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
          '& td, & th': { py: 1.5, borderBottom: 'none' },
          // Optional: Add explicit height if you ever move towards FixedSizeList virtualization
          // minHeight: '50px', // Example fixed height
        }}
      >
        <TableCell component="th" scope="row" sx={{ width: '25%' }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" noWrap sx={{ flexGrow: 1, fontWeight: 500 }}>
              {effectiveUser ? <Button onClick={handleUserClick} sx={{ fontWeight: "normal", p: 0, minWidth: 0 }}>{effectiveUser}</Button> : 'System'} {/* Added Button styling */}
            </Typography>
            {effectiveUser && (
              <Tooltip title={`View user ${ effectiveUser }`}>
                <IconButton size="small" onClick={handleUserClick} aria-label={`View user ${ effectiveUser }`} sx={{ p: 0 }}> {/* Added padding: 0 */}
                  <LinkIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        <TableCell align="left" sx={{ width: '50%' }}>
          {eventDetailsContent}
        </TableCell>

        <TableCell align="left" sx={{ width: '50%' }}>
          <ScoreCell userScore={userScore?.traderScore} title="trader" />
        </TableCell>


        <TableCell align="right" sx={{ width: '25%' }}>
          <Tooltip title={moment(time).format('lll')}>
            <Typography variant="caption">
              {moment(time).fromNow()}
            </Typography>
          </Tooltip>
        </TableCell>

      </TableRow>

      {/* QuickBuy Row */}
      {showQuickBuy && effectiveTokenId && (
        <TableRow
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ '& > *': { borderBottom: 'unset', p: 0 } }}
        >
          <TableCell colSpan={3}>
            <Collapse in={isHovering} timeout="auto" unmountOnExit>
              <Box sx={{ mx: 2, my: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, boxShadow: 1 }}>
                <QuickBuy tokenId={effectiveTokenId} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}); // End of React.memo(EventRow)


// --- Child Component: Filter Controls (MODIFIED FOR DEBOUNCING) ---
// No changes needed here, it correctly manages local state and debounces updates to parent.

function FilterControls({
  initialFilters,
  onFilterChangeDebounced,
  paused,
  onPauseToggle,
  isConnected
}) {
  const [localFilters, setLocalFilters] = useState(initialFilters);
  const debounceTimeoutRef = useRef(null);

  const handleLocalInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleLocalSelectChange = useCallback((event) => {
    setLocalFilters(prev => ({ ...prev, type: event.target.value }));
  }, []);

  // Effect to debounce filter changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      onFilterChangeDebounced(localFilters);
    }, FILTER_DEBOUNCE_MS);

    // Cleanup: Clear the timeout when localFilters change or component unmounts
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localFilters, onFilterChangeDebounced]); // Depend on localFilters and the debounced callback

  // Effect to sync local state if initialFilters prop changes from parent
  useEffect(() => {
    setLocalFilters(initialFilters);
  }, [initialFilters]);


  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        flexWrap="wrap"
        justifyContent="space-between"
      >
        {/* Filters Section */}
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {/* Connection Status */}
          <Tooltip title={isConnected ? "Live Feed Connected" : "Live Feed Disconnected"}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: isConnected ? "success.main" : "error.main" }} />
              <Typography variant="body2" >
                {isConnected ? "Live" : "Offline"}
              </Typography>
            </Stack>
          </Tooltip>
          {/* User Filter - USES LOCAL STATE/HANDLER */}
          <TextField
            label="Filter User" name="user" variant="outlined" size="small"
            value={localFilters.user}
            onChange={handleLocalInputChange}
            sx={{ minWidth: '160px', flexGrow: { xs: 1, sm: 0 } }}
          />
          {/* Type Filter - USES LOCAL STATE/HANDLER */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Event Type</InputLabel>
            <Select label="Event Type" name="type"
              value={localFilters.type}
              onChange={handleLocalSelectChange}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="sell">Sell</MenuItem>
              <MenuItem value="buy">Buy</MenuItem>
              <MenuItem value="create">Create</MenuItem>
            </Select>
          </FormControl>
          {/* BTC Filter - USES LOCAL STATE/HANDLER */}
          <TextField
            label="Min" name="btc" variant="outlined" size="small" type="number"
            inputProps={{ step: "0.0001", min: "0" }}
            value={localFilters.btc}
            onChange={handleLocalInputChange}
            InputProps={{ endAdornment: <InputAdornment position="end" color="grey.900"><Typography variant="body2">BTC</Typography></InputAdornment> }}
            sx={{ width: '140px' }}
          />
        </Stack>
        {/* Pause Button Section */}
        <Box sx={{ pt: { xs: 1, md: 0 } }}>
          <Tooltip title={paused ? "Resume live feed" : "Pause live feed"}>
            <Button variant="outlined" color="primary" onClick={onPauseToggle}>
              {paused ? "Resume" : "Pause"}
            </Button>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );
}


// --- Main Component: Event Stream (MODIFIED FOR BATCHING) ---

function EventStreamComponent() {
  const [eventData, setEventData] = useState([]); // State for displayed events
  const eventQueueRef = useRef([]); // Ref to queue incoming events
  const batchTimerRef = useRef(null); // Ref to hold the batch timer ID

  // State for filters and UI status
  const [filters, setFilters] = useState({ user: "", type: "all", btc: "" });
  const [paused, setPaused] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to track paused state inside effects without recreating effects
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
    // When pausing/resuming, process any events currently in the queue immediately
    // if unpausing, to update the view quickly.
    if (!paused && eventQueueRef.current.length > 0) {
      processEventQueue();
    }
  }, [paused]); // Depend on paused state

  // --- Function to process the event queue ---
  // This function will be called periodically by the timer
  const processEventQueue = useCallback(() => {
    // If paused, do not process the queue. Events will accumulate.
    if (pausedRef.current) {
      // console.log("Queue paused, not processing.");
      return;
    }

    // If there are no events in the queue, stop the timer.
    if (eventQueueRef.current.length === 0) {
      if (batchTimerRef.current) {
        clearInterval(batchTimerRef.current);
        batchTimerRef.current = null;
        // console.log("Queue empty, timer stopped.");
      }
      return;
    }

    // Take all events from the queue
    const newEvents = [...eventQueueRef.current];
    eventQueueRef.current = []; // Clear the queue

    // Update the state with the new events
    setEventData(prev => {
      const combinedEvents = [...newEvents, ...prev];
      // Keep only the most recent MAX_EVENTS
      return combinedEvents.slice(0, MAX_EVENTS);
    });

    // console.log(`Processed batch of ${newEvents.length} events. Total displayed: ${eventData.length + newEvents.length}.`);

    // After processing, if the queue received more events *while* we were processing,
    // the timer should continue running. The check at the start of the *next* interval
    // will stop the timer if the queue is then empty.

  }, []); // Dependencies: None needed as it uses refs and state setter

  // --- EventSource Connection Effect (MODIFIED FOR BATCHING) ---
  useEffect(() => {
    setIsLoading(true);
    console.log("Connecting to SSE...");
    const eventSource = new EventSource(`${ API_BASE_URL }/event/stream`);

    eventSource.onopen = () => {
      setIsLoading(false);
      setIsConnected(true);
      console.log("SSE Connected");
      // Start the batch processing timer when connected if it's not already running
      if (!batchTimerRef.current) {
        batchTimerRef.current = setInterval(processEventQueue, BATCH_PROCESS_INTERVAL_MS);
        console.log("Batch processing timer started.");
      }
    };

    eventSource.onerror = (err) => {
      setIsLoading(false);
      setIsConnected(false);
      console.error("SSE Error:", err);
      // Stop the timer and close SSE on error
      if (batchTimerRef.current) {
        clearInterval(batchTimerRef.current);
        batchTimerRef.current = null;
        console.log("SSE error, timer stopped.");
      }
      eventSource.close();
    };

    eventSource.onmessage = (event) => {
      // Add incoming event to the queue, but *don't* update state immediately
      try {
        const newEvent = JSON.parse(event.data);
        // Add a fallback 'create' type if user/creator/id exists but type is missing
        if (!newEvent.type && (newEvent.creator || newEvent.user) && newEvent.id) {
          newEvent.type = 'create';
        }

        eventQueueRef.current.push(newEvent);
        // console.log("Event added to queue. Queue size:", eventQueueRef.current.length);

        // Ensure the timer is running if events are arriving
        if (!batchTimerRef.current) {
          batchTimerRef.current = setInterval(processEventQueue, BATCH_PROCESS_INTERVAL_MS);
          // console.log("Timer was stopped, restarted on new message.");
        }

      } catch (error) {
        console.error("SSE Parse Error:", error, event.data);
      }
    };

    // Cleanup: Close SSE and clear the timer on unmount
    return () => {
      eventSource.close();
      setIsConnected(false);
      console.log("SSE Closed (component unmount)");
      if (batchTimerRef.current) {
        clearInterval(batchTimerRef.current);
        batchTimerRef.current = null;
        console.log("Batch processing timer stopped (component unmount)");
      }
      // Optionally clear the queue on unmount
      eventQueueRef.current = [];
    };
  }, [processEventQueue]); // Depend on processEventQueue so the effect gets the latest version

  // --- Filter Change Handler (Debounced) ---
  const handleFilterChangeDebounced = useCallback((debouncedFilters) => {
    setFilters(debouncedFilters);
  }, []); // setFilters is stable

  // --- Pause/Resume Handler ---
  const handlePauseToggle = useCallback(() => setPaused(p => !p), []); // setPaused is stable

  // --- Memoized Filtering Logic (Runs when eventData state or filters state changes) ---
  const filteredData = useMemo(() => {
    const minBTC = parseFloat(filters.btc);
    const hasMinBTC = !isNaN(minBTC) && minBTC > 0;
    // console.log(`Filtering ${eventData.length} events with:`, filters); // Keep for debugging filtering itself if needed
    return eventData.filter((event) => {
      // Filter by user (case-insensitive, check user or creator)
      if (filters.user && !(event.user || event.creator || "").toLowerCase().includes(filters.user.toLowerCase())) {
        return false;
      }
      // Filter by type
      if (filters.type !== "all" && event.type !== filters.type) {
        return false;
      }
      // Filter by minimum BTC amount (only applies to sell/buy)
      if (hasMinBTC) {
        if (event.type === "sell" || event.type === "buy") {
          // Ensure transformToSats is correctly used and matches the expected 'amount_btc' format
          const btcValue = typeof event.amount_btc === 'string'
            ? parseFloat(event.amount_btc) // If amount_btc is already BTC string
            : transformToSats(event.amount_btc) / 100_000_000; // If amount_btc is sats (number or string)

          if (isNaN(btcValue) || btcValue < minBTC) {
            return false;
          }
        } else {
          return false; // Exclude non-buy/sell if BTC filter active
        }
      }
      return true; // Keep the event if it passes all filters
    });
  }, [eventData, filters]); // Dependencies are correct

  // --- Component Render ---
  return (
    <Box sx={{ width: "100%", mx: "auto" }}>
      {/* Filter Controls */}
      <FilterControls
        initialFilters={filters} // Pass current state filters as initial to local state in child
        onFilterChangeDebounced={handleFilterChangeDebounced} // Callback to update filters state (debounced)
        paused={paused}
        onPauseToggle={handlePauseToggle}
        isConnected={isConnected}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {/* Removed background from header cells - stickyHeader often handles this */}
              <TableCell sx={{ background: "black", fontWeight: 'bold', width: '25%', borderBottom: '1px solid', borderColor: 'divider' }}>User</TableCell>
              <TableCell sx={{ background: "black", fontWeight: 'bold', width: '50%', borderBottom: '1px solid', borderColor: 'divider' }} align="left">Details</TableCell>
              <TableCell sx={{ background: "black", fontWeight: 'bold', width: '50%', borderBottom: '1px solid', borderColor: 'divider' }} align="left">Trader Score</TableCell>
              <TableCell sx={{ background: "black", fontWeight: 'bold', width: '25%', borderBottom: '1px solid', borderColor: 'divider' }} align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6, border: 0 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography variant="body2" component="span">Connecting to feed...</Typography> {/* More specific message */}
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 && eventData.length > 0 ? ( // Show "No events match filters" only if there are events but they are filtered out
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6, border: 0 }}>
                  <Stack alignItems="center" spacing={1}>
                    <InfoOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                    <Typography >
                      No events match filters
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 && eventData.length === 0 ? ( // Show "Waiting for events" if no events have arrived yet
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 6, border: 0 }}>
                  <Stack alignItems="center" spacing={1}>
                    <InfoOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                    <Typography >
                      Waiting for events...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              // Render the filtered events using the memoized row component
              filteredData.map((event) => (
                // *** IMPORTANT FIX: Use a stable, unique ID for the key prop ***
                // event._id or event.id are preferred as stable identifiers.
                // Fallback to event.time if no ID exists, but using array index is bad here.
                <MemoizedEventRow key={event._id || event.id || event.time} eventData={event} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EventStreamComponent;